import { db } from '../config/firebase';
import { doc, getDoc, setDoc, deleteDoc, collection, getDocs, query, where } from 'firebase/firestore';

export interface UserFavorite {
  userId: string;
  videoId: string;
  videoTitle: string;
  videoModule?: number;
  videoCategory?: string;
  favoritedAt: Date;
}

// Add a video to user's favorites
export async function addToFavorites(userId: string, videoId: string, videoData: {
  title: string;
  module?: number;
  category?: string;
}): Promise<void> {
  try {
    const favoriteRef = doc(db, 'userFavorites', `${userId}_${videoId}`);
    await setDoc(favoriteRef, {
      userId,
      videoId,
      videoTitle: videoData.title,
      videoModule: videoData.module,
      videoCategory: videoData.category,
      favoritedAt: new Date()
    });
  } catch (error) {
    console.error('Error adding to favorites:', error);
    throw error;
  }
}

// Remove a video from user's favorites
export async function removeFromFavorites(userId: string, videoId: string): Promise<void> {
  try {
    const favoriteRef = doc(db, 'userFavorites', `${userId}_${videoId}`);
    await deleteDoc(favoriteRef);
  } catch (error) {
    console.error('Error removing from favorites:', error);
    throw error;
  }
}

// Get all user's favorite videos
export async function getUserFavorites(userId: string): Promise<UserFavorite[]> {
  try {
    const q = query(
      collection(db, 'userFavorites'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      favoritedAt: doc.data().favoritedAt.toDate()
    })) as UserFavorite[];
  } catch (error) {
    console.error('Error getting user favorites:', error);
    throw error;
  }
}

// Check if a video is favorited by user
export async function isVideoFavorited(userId: string, videoId: string): Promise<boolean> {
  try {
    const favoriteRef = doc(db, 'userFavorites', `${userId}_${videoId}`);
    const favoriteDoc = await getDoc(favoriteRef);
    return favoriteDoc.exists();
  } catch (error) {
    console.error('Error checking if video is favorited:', error);
    return false;
  }
}

// Get user's favorite video IDs as a set for quick lookup
export async function getUserFavoriteVideoIds(userId: string): Promise<Set<string>> {
  try {
    const favorites = await getUserFavorites(userId);
    return new Set(favorites.map(fav => fav.videoId));
  } catch (error) {
    console.error('Error getting user favorite video IDs:', error);
    return new Set();
  }
}
